import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../../../services/api';
import * as fileApi from '../../../../services/fileApi';
import LoadingSpinner from '../../../Common/LoadingSpinner';
import ProofAttachmentDropzone from '../../../Common/ProofAttachmentDropzone';
import {
  CreditCardIcon,
  BanknotesIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  AcademicCapIcon,
  UserIcon,
  CalendarIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { getSlimLogoPath } from '../../../../utils/slimLogo';
import { toPaymentDateTimeIso } from '../../../../utils/crmPaymentFinanceDefaults';

/**
 * Public page for students to submit installment payments
 */
const PublicEnrollmentPayment = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [enrollment, setEnrollment] = useState(null);
  const [installmentDetails, setInstallmentDetails] = useState(null);
  const [repeatChargeDetails, setRepeatChargeDetails] = useState(null);
  const [targetType, setTargetType] = useState('installment');
  const [publicTargetState, setPublicTargetState] = useState({
    paymentLinkClosed: false,
    submittedPayment: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [linkExpired, setLinkExpired] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [initiatingCard, setInitiatingCard] = useState(false);
  
  // Payment form state
  const [paymentData, setPaymentData] = useState({
    actualAmount: '',
    paymentMethod: 'Bank Transfer',
    paymentDate: new Date().toISOString().split('T')[0],
    referenceNumber: '',
    bankName: '',
    notes: ''
  });
  
  const [studentCredit, setStudentCredit] = useState(null);
  const [applyCredit, setApplyCredit] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Handle Paycorp return params on mount
  useEffect(() => {
    const cardPaymentResult = searchParams.get('cardPayment');
    if (cardPaymentResult === 'success') {
      toast.success('Payment processed successfully!');
    } else if (cardPaymentResult === 'failed') {
      toast.error('Card payment failed. Please try again or use a different payment method.');
    } else if (cardPaymentResult === 'cancelled') {
      toast('Card payment cancelled.', { icon: 'ℹ️' });
    }
  }, []);

  useEffect(() => {
    fetchEnrollmentByToken();
  }, [token]);

  const fetchEnrollmentByToken = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch enrollment using public token-based endpoint (no auth required)
      const response = await api.get(`/crm/enrollments/public/${token}`);
      const enrollmentData = response.data.data || response.data;
      setEnrollment(enrollmentData);

      const creditInfo = enrollmentData.studentCredit || null;
      setStudentCredit(creditInfo);
      const shouldApplyCredit = Boolean(creditInfo?.canApplyCredit && creditInfo?.maxCreditApplicable > 0);
      setApplyCredit(shouldApplyCredit);

      const publicTarget = enrollmentData.publicTarget || {};
      const resolvedTargetType = publicTarget.targetType || 'installment';
      setTargetType(resolvedTargetType);
      setPublicTargetState({
        paymentLinkClosed: Boolean(publicTarget.paymentLinkClosed),
        submittedPayment: publicTarget.submittedPayment || null
      });

      if (resolvedTargetType === 'repeat-charge') {
        const repeatCharge = publicTarget.repeatCharge || null;
        if (!repeatCharge) {
          throw new Error('Repeat charge not found');
        }

        setRepeatChargeDetails(repeatCharge);
        setInstallmentDetails(null);
        const baseDue = Number(repeatCharge?.payment?.balanceAmount || repeatCharge?.payment?.totalAmount || 0);
        const netDue = shouldApplyCredit
          ? Math.max(0, baseDue - (Number(creditInfo?.maxCreditApplicable) || 0))
          : baseDue;
        setPaymentData((prev) => ({
          ...prev,
          actualAmount: netDue > 0 ? String(netDue) : '0',
          paymentMethod: netDue === 0 ? 'Credit Balance' : (prev.paymentMethod === 'Credit Balance' ? 'Bank Transfer' : prev.paymentMethod),
          referenceNumber: enrollmentData.enrollmentNumber || ''
        }));
        return;
      }

      if (resolvedTargetType === 'full') {
        setInstallmentDetails(null);
        setRepeatChargeDetails(null);
        const baseDue = Number(publicTarget.amount || enrollmentData.payment?.balanceAmount || 0);
        const netDue = shouldApplyCredit
          ? Math.max(0, baseDue - (Number(creditInfo?.maxCreditApplicable) || 0))
          : baseDue;
        setPaymentData((prev) => ({
          ...prev,
          actualAmount: netDue > 0 ? String(netDue) : '0',
          paymentMethod: netDue === 0 ? 'Credit Balance' : (prev.paymentMethod === 'Credit Balance' ? 'Bank Transfer' : prev.paymentMethod),
          referenceNumber: enrollmentData.enrollmentNumber || ''
        }));
        return;
      }

      const installment = publicTarget.installment || null;

      if (!installment) {
        throw new Error('Installment not found');
      }

      setInstallmentDetails(installment);
      setRepeatChargeDetails(null);
      const baseDue = Number(installment.amount || 0);
      const netDue = shouldApplyCredit
        ? Math.max(0, baseDue - (Number(creditInfo?.maxCreditApplicable) || 0))
        : baseDue;
      setPaymentData(prev => ({
        ...prev,
        actualAmount: netDue > 0 ? String(netDue) : '0',
        paymentMethod: netDue === 0 ? 'Credit Balance' : (prev.paymentMethod === 'Credit Balance' ? 'Bank Transfer' : prev.paymentMethod),
        referenceNumber: enrollmentData.enrollmentNumber || ''
      }));
    } catch (error) {
      console.error('Error fetching enrollment:', error);
      if (error.response?.status === 403 || error.response?.data?.code === 'LINK_EXPIRED' || (error.response?.data?.message || '').toLowerCase().includes('expired')) {
        setLinkExpired(true);
      } else {
        setError(error.response?.data?.message || 'Invalid or expired payment link. Please contact the institution.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCredit = (enabled) => {
    setApplyCredit(enabled);
    const expected = targetType === 'repeat-charge'
      ? Number(repeatChargeDetails?.payment?.balanceAmount || repeatChargeDetails?.payment?.totalAmount || 0)
      : targetType === 'full'
        ? Number(enrollment?.payment?.balanceAmount || 0)
        : Number(installmentDetails?.amount || 0);

    const maxApplicable = Number(studentCredit?.maxCreditApplicable) || Math.min(Number(studentCredit?.creditBalance) || 0, expected);

    if (enabled) {
      const net = Math.max(0, expected - maxApplicable);
      setPaymentData(prev => ({
        ...prev,
        actualAmount: String(net),
        paymentMethod: net === 0 ? 'Credit Balance' : (prev.paymentMethod === 'Credit Balance' ? 'Bank Transfer' : prev.paymentMethod)
      }));
    } else {
      setPaymentData(prev => ({
        ...prev,
        actualAmount: String(expected),
        paymentMethod: prev.paymentMethod === 'Credit Balance' ? 'Bank Transfer' : prev.paymentMethod
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Reference is always locked to enrollment number
    if (name === 'referenceNumber') return;
    setPaymentData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const isRepeatChargeMode = targetType === 'repeat-charge';
  const isFullBalanceMode = targetType === 'full';
  const activeCreditToApply = applyCredit ? (Number(studentCredit?.maxCreditApplicable) || 0) : 0;
  const currentEnteredAmount = parseFloat(paymentData.actualAmount || 0);
  const isCreditOnly = activeCreditToApply > 0 && currentEnteredAmount <= 0;

  const isCardPayment = isRepeatChargeMode &&
    ['Card', 'Credit Card', 'Debit Card'].includes(paymentData.paymentMethod);
  const methodRequiresReceipt = !isCreditOnly && !['Card', 'Credit Card', 'Debit Card', 'Cash', 'Credit Balance'].includes(paymentData.paymentMethod);

  const handleInitiateCardPayment = async () => {
    try {
      setInitiatingCard(true);
      const response = await api.post(`/crm/enrollments/public/${token}/initiate-card`);
      const paymentPageUrl = response?.data?.data?.paymentPageUrl;
      if (!paymentPageUrl) {
        toast.error('Failed to get payment page URL. Please try again.');
        return;
      }
      window.location.href = paymentPageUrl;
    } catch (error) {
      console.error('Card payment initiation error:', error);
      toast.error(error.response?.data?.message || 'Failed to initiate card payment. Please try again.');
      setInitiatingCard(false);
    }
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();

    // Consent required for all payment methods
    if (!agreedToTerms) {
      toast.error('Please agree to the payment terms before proceeding');
      return;
    }

    // For card payments, redirect to Paycorp
    if (isCardPayment) {
      await handleInitiateCardPayment();
      return;
    }

    // Validation
    const creditApplied = applyCredit ? (Number(studentCredit?.maxCreditApplicable) || 0) : 0;
    const enteredAmount = Number.isFinite(parseFloat(paymentData.actualAmount)) ? parseFloat(paymentData.actualAmount) : 0;
    const totalCovered = enteredAmount + creditApplied;

    if (totalCovered <= 0 || enteredAmount < 0) {
      toast.error('Please enter a valid payment amount or apply available credit balance');
      return;
    }

    if (isRepeatChargeMode) {
      const balance = Number(repeatChargeDetails?.payment?.balanceAmount || repeatChargeDetails?.payment?.totalAmount || 0);
      if (balance > 0 && totalCovered - balance > 0.01) {
        toast.error(`Total covered amount cannot exceed the outstanding balance of ${(repeatChargeDetails?.payment?.currency || 'LKR')} ${balance.toLocaleString()}`);
        return;
      }
    }

    if (isFullBalanceMode) {
      const balance = Number(enrollment?.payment?.balanceAmount || publicTargetState?.amount || 0);
      if (balance > 0 && totalCovered - balance > 0.01) {
        toast.error(`Total covered amount cannot exceed the outstanding balance of ${(enrollment?.payment?.currency || 'LKR')} ${balance.toLocaleString()}`);
        return;
      }
    }

    if (!isCreditOnly && !paymentData.paymentMethod) {
      toast.error('Please select a payment method');
      return;
    }

    if (methodRequiresReceipt && !receiptFile) {
      toast.error('Please upload payment receipt/proof');
      return;
    }

    if (!agreedToTerms) {
      toast.error('Please confirm the payment declaration before submitting');
      return;
    }

    try {
      setSubmitting(true);

      // Upload receipt first (only when required and provided)
      let documentId = null;
      if (methodRequiresReceipt && receiptFile) {
        setUploadingReceipt(true);
        const uploadResponse = await fileApi.uploadFilePublic(receiptFile, { 
          category: 'Payment Receipt',
          description: targetType === 'repeat-charge'
            ? `Repeat charge payment receipt`
            : targetType === 'full'
              ? `Enrollment payment receipt`
            : `Installment ${installmentDetails.installmentNumber} payment receipt`
        });
        documentId = uploadResponse.data.data.fileId;
        setUploadingReceipt(false);
      }

      // Submit payment using public token-based endpoint (no auth required)
      const payload = {
        amount: enteredAmount,
        creditAmountApplied: creditApplied,
        paymentMethod: (enteredAmount === 0 && creditApplied > 0) ? 'Credit Balance' : paymentData.paymentMethod,
        referenceNumber: paymentData.referenceNumber,
        notes: targetType === 'repeat-charge'
          ? `Repeat charge payment. ${paymentData.notes}`
          : targetType === 'full'
            ? `Full balance payment. ${paymentData.notes}`
          : `Installment ${installmentDetails.installmentNumber} payment. ${paymentData.notes}`,
        paymentDate: toPaymentDateTimeIso(paymentData.paymentDate),
        installmentNumber: targetType === 'installment' ? installmentDetails.installmentNumber : undefined,
        receiptDocument: documentId ? {
          fileId: documentId,
          fileName: receiptFile.name,
          fileSize: receiptFile.size,
          mimeType: receiptFile.type,
          documentType: 'Payment Receipt'
        } : null
      };

      await api.post(`/crm/enrollments/public/${token}/payment`, payload);

      toast.success('Payment submitted successfully! Your payment will be verified shortly.');
      
      // Refresh data
      await fetchEnrollmentByToken();
      
      // Clear form
      setReceiptFile(null);
      setAgreedToTerms(false);
    } catch (error) {
      console.error('Payment submission error:', error);
      toast.error(error.response?.data?.message || 'Failed to submit payment. Please try again.');
    } finally {
      setSubmitting(false);
      setUploadingReceipt(false);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading payment information..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <ExclamationCircleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Link</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <a
            href="/"
            className="inline-block px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Go to Homepage
          </a>
        </div>
      </div>
    );
  }

  if (linkExpired) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <ClockIcon className="w-16 h-16 text-amber-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Link Expired</h2>
          <p className="text-gray-600 mb-6">
            This payment link has expired. Please contact the institution to request a new payment link.
          </p>
          <a
            href="/"
            className="inline-block px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Go to Homepage
          </a>
        </div>
      </div>
    );
  }

  // isRepeatChargeMode is declared earlier (before handleSubmitPayment) — do not redeclare here

  if (publicTargetState.paymentLinkClosed) {
    const submittedAmount = Number(publicTargetState.submittedPayment?.amount) || 0;
    const submittedCurrency = isRepeatChargeMode
      ? (repeatChargeDetails?.payment?.currency || enrollment?.payment?.currency || 'LKR')
      : (enrollment?.payment?.currency || 'LKR');

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Submitted</h2>
          <p className="text-gray-600 mb-6">
            This payment link has been closed after submission. The institution can generate a new payment link if an additional balance remains.
          </p>
          <div className="bg-gray-50 rounded-lg p-4 text-left">
            <p className="text-sm text-gray-600 mb-1">Amount Submitted</p>
            <p className="text-2xl font-bold text-gray-900">
              {submittedCurrency} {submittedAmount.toLocaleString()}
            </p>
            {publicTargetState.submittedPayment?.referenceNumber && (
              <p className="text-sm text-gray-600 mt-3">
                Reference: <span className="font-medium">{publicTargetState.submittedPayment.referenceNumber}</span>
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Show "under review" screen if there's a pending payment submitted via public link
  if (!publicTargetState.paymentLinkClosed && publicTargetState.submittedPayment?.status === 'Pending') {
    const pending = publicTargetState.submittedPayment;
    const currency = isRepeatChargeMode
      ? (repeatChargeDetails?.payment?.currency || 'LKR')
      : (enrollment?.payment?.currency || 'LKR');
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <ClockIcon className="w-16 h-16 text-amber-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Under Review</h2>
          <p className="text-gray-600 mb-6">
            Your payment has been submitted and is currently under review. You will be notified once it has been verified.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left space-y-2">
            {pending.amount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Amount Submitted</span>
                <span className="font-semibold">{currency} {Number(pending.amount).toLocaleString()}</span>
              </div>
            )}
            {pending.referenceNumber && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Reference</span>
                <span className="font-medium">{pending.referenceNumber}</span>
              </div>
            )}
            {pending.paymentDate && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Payment Date</span>
                <span className="font-medium">{new Date(pending.paymentDate).toLocaleDateString()}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (isRepeatChargeMode && Number(repeatChargeDetails?.payment?.balanceAmount || 0) <= 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Already Completed</h2>
          <p className="text-gray-600 mb-6">
            This repeat charge has already been fully paid.
          </p>
          <div className="bg-gray-50 rounded-lg p-4 text-left">
            <p className="text-sm text-gray-600 mb-1">{repeatChargeDetails?.chargeType === 'INTAKE_REPEAT' ? 'Intake Repeat' : repeatChargeDetails?.chargeType === 'MODULE_REPEAT' ? 'Module Repeat' : 'Exam Repeat'}</p>
            <p className="text-lg font-semibold text-gray-900 mb-1">{repeatChargeDetails?.targetIntakeName || repeatChargeDetails?.moduleName || repeatChargeDetails?.examName}</p>
            <p className="text-2xl font-bold text-gray-900">
              {repeatChargeDetails?.payment?.currency || enrollment.payment?.currency || 'LKR'} {(repeatChargeDetails?.payment?.totalAmount || 0).toLocaleString()}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // If installment already paid
  if (!isRepeatChargeMode && !isFullBalanceMode && installmentDetails?.status === 'Paid') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Already Completed</h2>
          <p className="text-gray-600 mb-6">
            This installment has already been paid and verified.
          </p>
          <div className="bg-gray-50 rounded-lg p-4 text-left">
            <p className="text-sm text-gray-600 mb-1">Installment {installmentDetails.installmentNumber}</p>
            <p className="text-2xl font-bold text-gray-900">
              {enrollment.payment?.currency || 'LKR'} {(installmentDetails.amount || 0).toLocaleString()}
            </p>
            {installmentDetails.paidDate && (
              <p className="text-xs text-gray-500 mt-2">
                Paid on {new Date(installmentDetails.paidDate).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-6 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-4">
          <div className="flex items-center gap-3">
            <img src={getSlimLogoPath()} alt="SLIM ERP Logo" className="h-14 w-auto" />
            <div className='pl-4'>
              <h1 className="text-xl font-bold text-gray-900">
                {isRepeatChargeMode
                  ? 'Submit Repeat Charge Payment'
                  : isFullBalanceMode
                    ? 'Submit Enrollment Payment'
                    : 'Submit Installment Payment'}
              </h1>
              <p className="text-sm text-gray-600">
                {isRepeatChargeMode
                  ? 'Submit your repeat charge payment details and upload proof. Verification within 24-48 hours.'
                  : isFullBalanceMode
                    ? 'Submit your payment details and upload proof. Staff will review and approve the payment.'
                  : 'Submit your installment payment details and upload proof. Verification within 24-48 hours.'}
              </p>
            </div>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Left Column - Payment Information */}
          <div className="space-y-4">
            {/* Payment Amount Info */}
            <div className="bg-white rounded-lg shadow-md p-4">
              <h2 className="text-base font-semibold text-gray-900 mb-3">Payment Information</h2>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-indigo-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">
                    {isRepeatChargeMode
                      ? (repeatChargeDetails?.chargeType === 'INTAKE_REPEAT' ? 'Intake Repeat Charge' : repeatChargeDetails?.chargeType === 'MODULE_REPEAT' ? 'Module Repeat Charge' : 'Exam Repeat Charge')
                      : isFullBalanceMode
                        ? 'Outstanding Balance'
                      : `Installment ${installmentDetails?.installmentNumber}`}
                  </p>
                  <p className="text-2xl font-bold text-indigo-600">
                    {(isRepeatChargeMode ? repeatChargeDetails?.payment?.currency : enrollment.payment?.currency) || 'LKR'} {(
                      isRepeatChargeMode
                        ? (repeatChargeDetails?.payment?.balanceAmount || repeatChargeDetails?.payment?.totalAmount || 0)
                        : isFullBalanceMode
                          ? (enrollment.payment?.balanceAmount || 0)
                        : (installmentDetails?.amount || 0)
                    ).toLocaleString()}
                  </p>
                </div>
                {!isRepeatChargeMode && installmentDetails?.dueDate && (
                  <div className="bg-orange-50 rounded-lg p-3">
                    <p className="text-xs text-gray-600 mb-1">Due Date</p>
                    <p className="text-lg font-semibold text-orange-600">
                      {new Date(installmentDetails.dueDate).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>

              {/* Enrollment Details */}
              <div className="border-t border-gray-200 pt-3">
                <h3 className="text-xs font-semibold text-gray-700 mb-2">Enrollment Details</h3>
                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">Student</span>
                    <span className="font-medium">{enrollment.studentInfo?.name}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">Enrollment Number</span>
                    <span className="font-medium">{enrollment.enrollmentNumber}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">Program</span>
                    <span className="font-medium">{enrollment.subProductId?.name || 'N/A'}</span>
                  </div>
                  {isRepeatChargeMode ? (
                    <>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Charge Type</span>
                        <span className="font-medium">{repeatChargeDetails?.chargeType === 'INTAKE_REPEAT' ? 'Intake Repeat' : repeatChargeDetails?.chargeType === 'MODULE_REPEAT' ? 'Module Repeat' : 'Exam Repeat'}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Assessment</span>
                        <span className="font-medium">{repeatChargeDetails?.targetIntakeName || repeatChargeDetails?.moduleName || repeatChargeDetails?.examName || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Outstanding</span>
                        <span className="font-medium">
                          {(repeatChargeDetails?.payment?.currency || 'LKR')} {(repeatChargeDetails?.payment?.balanceAmount || 0).toLocaleString()}
                        </span>
                      </div>
                    </>
                  ) : installmentDetails?.description && (
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Description</span>
                      <span className="font-medium">{installmentDetails.description}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Bank Transfer Instructions */}
            {(paymentData.paymentMethod === 'Bank Transfer' || paymentData.paymentMethod === 'Cheque') && (() => {
              const bankInfo = enrollment.bankAccountInfo || enrollment.applicationId?.bankAccountInfo;
              if (!bankInfo?.accountNumber) return null;
              return (
              <div className="bg-white rounded-lg shadow-md p-4">
                <h2 className="text-base font-semibold text-gray-900 mb-3">Bank Transfer Instructions</h2>
                <p className="text-xs text-gray-500 mb-3">Please transfer to the following account:</p>
                <div className="space-y-2 text-sm">
                  {bankInfo.bankName && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Bank</span>
                      <span className="font-medium">{bankInfo.bankName}</span>
                    </div>
                  )}
                  {bankInfo.branchName && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Branch</span>
                      <span className="font-medium">{bankInfo.branchName}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-500">Account Number</span>
                    <span className="font-semibold font-mono tracking-wide">{bankInfo.accountNumber}</span>
                  </div>
                  {bankInfo.accountHolderName && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Account Name</span>
                      <span className="font-medium">{bankInfo.accountHolderName}</span>
                    </div>
                  )}
                </div>
              </div>
            );
            })()}

            {/* Receipt Upload — hidden for card payments and cash */}
            {!isCardPayment && methodRequiresReceipt && (
              <div className="bg-white rounded-lg shadow-md p-4">
                <ProofAttachmentDropzone
                  file={receiptFile}
                  onChange={setReceiptFile}
                  required
                  hint={null}
                />
              </div>
            )}

            {/* Help Section */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-800">
                <strong>Note:</strong> After submission, your payment will be reviewed by the institution. You will be notified once the payment is verified.
              </p>
            </div>
          </div>

          {/* Right Column - Payment Form */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <h2 className="text-base font-semibold text-gray-900 mb-3">Payment Details</h2>

            {/* Student Credit Balance Section */}
            {studentCredit?.canApplyCredit && studentCredit?.maxCreditApplicable > 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center font-bold text-xs">
                      ✓
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-emerald-900">Student Credit Balance Available</p>
                      <p className="text-xs text-emerald-700">
                        Available: {((isRepeatChargeMode ? repeatChargeDetails?.payment?.currency : enrollment.payment?.currency) || 'LKR')} {Number(studentCredit.creditBalance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>
                  <label className="flex items-center space-x-2 cursor-pointer select-none bg-white px-2.5 py-1 rounded border border-emerald-300 hover:bg-emerald-50">
                    <input
                      type="checkbox"
                      checked={applyCredit}
                      onChange={(e) => handleToggleCredit(e.target.checked)}
                      className="rounded border-emerald-400 text-emerald-600 focus:ring-emerald-500 h-4 w-4"
                    />
                    <span className="text-xs font-semibold text-emerald-900">Apply Credit</span>
                  </label>
                </div>
                {applyCredit && (
                  <div className="bg-white/90 rounded p-2.5 text-xs text-emerald-900 space-y-1 border border-emerald-100">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Original Amount:</span>
                      <span>{((isRepeatChargeMode ? repeatChargeDetails?.payment?.currency : enrollment.payment?.currency) || 'LKR')} {Number(
                        isRepeatChargeMode
                          ? (repeatChargeDetails?.payment?.balanceAmount || repeatChargeDetails?.payment?.totalAmount || 0)
                          : isFullBalanceMode
                            ? (enrollment.payment?.balanceAmount || 0)
                          : (installmentDetails?.amount || 0)
                      ).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between text-emerald-700 font-medium">
                      <span>Credit Balance Applied:</span>
                      <span>- {((isRepeatChargeMode ? repeatChargeDetails?.payment?.currency : enrollment.payment?.currency) || 'LKR')} {Number(studentCredit.maxCreditApplicable).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="border-t border-emerald-100 pt-1 flex justify-between font-bold text-emerald-900">
                      <span>Net Cash to Pay:</span>
                      <span>{((isRepeatChargeMode ? repeatChargeDetails?.payment?.currency : enrollment.payment?.currency) || 'LKR')} {Number(studentCredit.netPayableAfterCredit).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <form onSubmit={handleSubmitPayment} className="space-y-3">
              {/* Amount & Payment Method Row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Amount Paid {isCreditOnly ? '(Covered by Credit)' : <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="number"
                    name="actualAmount"
                    value={paymentData.actualAmount}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    disabled={isCreditOnly}
                    required={!isCreditOnly}
                    className={`w-full px-2 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${isCreditOnly ? 'bg-gray-100 border-gray-200 text-gray-600 cursor-not-allowed' : 'border-gray-300'}`}
                    placeholder="Amount"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {applyCredit && studentCredit?.maxCreditApplicable > 0 ? (
                      <span className="text-emerald-700 font-medium">
                        Net expected: {((isRepeatChargeMode ? repeatChargeDetails?.payment?.currency : enrollment.payment?.currency) || 'LKR')} {Number(studentCredit.netPayableAfterCredit).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    ) : (
                      <>
                        Expected: {((isRepeatChargeMode ? repeatChargeDetails?.payment?.currency : enrollment.payment?.currency) || 'LKR')} {(
                          isRepeatChargeMode
                            ? (repeatChargeDetails?.payment?.balanceAmount || repeatChargeDetails?.payment?.totalAmount || 0)
                            : isFullBalanceMode
                              ? (enrollment.payment?.balanceAmount || 0)
                            : (installmentDetails?.amount || 0)
                        ).toLocaleString()}
                      </>
                    )}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Payment Method <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="paymentMethod"
                    value={paymentData.paymentMethod}
                    onChange={handleInputChange}
                    disabled={isCreditOnly}
                    required
                    className={`w-full px-2 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${isCreditOnly ? 'bg-gray-100 border-gray-200 text-gray-600 cursor-not-allowed' : 'border-gray-300'}`}
                  >
                    {isCreditOnly && (
                      <option value="Credit Balance">Credit Balance (100% Covered)</option>
                    )}
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="Online Payment">Online Payment</option>
                    {isRepeatChargeMode && (
                      <option value="Card">Card — Credit/Debit (via Paycorp)</option>
                    )}
                    <option value="Cheque">Cheque</option>
                    <option value="Mobile Payment">Mobile Payment</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              {/* Payment Date */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Payment Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="paymentDate"
                  value={paymentData.paymentDate}
                  onChange={handleInputChange}
                  required
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              {/* Reference Number & Bank Name Row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Reference Number
                  </label>
                  <input
                    type="text"
                    name="referenceNumber"
                    value={paymentData.referenceNumber}
                    readOnly
                    className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed"
                    title="Locked to enrollment number"
                  />
                  <p className="mt-1 text-[11px] text-gray-500">Locked to enrollment number</p>
                </div>
                {(paymentData.paymentMethod === 'Bank Transfer' || paymentData.paymentMethod === 'Cheque') && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Bank Name
                    </label>
                    <input
                      type="text"
                      name="bankName"
                      value={paymentData.bankName}
                      onChange={handleInputChange}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Bank name"
                    />
                  </div>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Additional Notes
                </label>
                <textarea
                  name="notes"
                  value={paymentData.notes}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Any additional information about the payment"
                />
              </div>

              {/* Payment Terms & Agreement */}
              <div className={`border rounded-lg p-4 ${agreedToTerms ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700 leading-relaxed">
                    <strong>I understand and agree</strong> that this payment submission and any amount paid towards this program are <strong>strictly non-refundable</strong> subject to institution policy. By continuing, I confirm that the payment amount, selected payment method, and any supporting documents I submit are accurate.
                  </span>
                </label>
                {!agreedToTerms && (
                  <p className="mt-2 text-xs font-medium text-red-600">
                    * You must agree to this before proceeding with payment.
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={submitting || uploadingReceipt || initiatingCard || !agreedToTerms}
                  className="w-full px-4 py-2.5 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {initiatingCard ? (
                    <>
                      <LoadingSpinner inline size="sm" color="text-white" center={false} />
                      <span>Redirecting to payment...</span>
                    </>
                  ) : submitting || uploadingReceipt ? (
                    <>
                      <LoadingSpinner inline size="sm" color="text-white" center={false} />
                      <span>{uploadingReceipt ? 'Uploading...' : 'Submitting...'}</span>
                    </>
                  ) : isCardPayment ? (
                    <>
                      <CreditCardIcon className="w-4 h-4" />
                      <span>Pay by Card</span>
                    </>
                  ) : (
                    <>
                      <CreditCardIcon className="w-4 h-4" />
                      <span>Submit Payment</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicEnrollmentPayment;
